// O que cada PAPEL vê no portal — o ÚNICO lugar que decide isso. A API nova não manda lista de
// permissões: cada rota dela diz quem passa (`exigirAdmin(...papeis)`, com o ADMIN sempre), e esta
// tabela espelha as rotas que o portal usa. É só apresentação: a guarda de verdade é do servidor, que
// responde 403 SEM_PERMISSAO a quem não pode.
//
// Mudou o `exigirAdmin` de uma rota na plugchef.admin.api? Mude aqui.
import type { NivelSuporte, Papel } from '../api/tipos';

/** O que o portal mostra ou esconde. O nome é o da tela/ação, não o da rota. */
//
// Trocar a própria senha (`POST /auth/senha`, exigirAdmin()) é de todos e não tem capacidade.
export type Capacidade =
  | 'equipe.ver' //            GET  /equipe                                         exigirAdmin()
  | 'equipe.gravar' //         POST/PATCH /equipe, zerar, encerrar, senha-provisória  exigirAdmin('ADMIN')
  | 'auditoria.ver' //         GET  /auditoria                                      exigirAdmin('ADMIN')
  | 'comercial.ver' //         GET  /comercial/contratantes[/:id], planos, itens, ciclos  exigirAdmin()
  | 'contratantes.gravar' //   POST/PATCH /comercial/contratantes[/:id]             exigirAdmin('COMERCIAL')
  | 'cliente.criar' //         POST /comercial/contratantes/:id/criar-cliente       exigirAdmin('COMERCIAL')
  | 'cliente.franqueadora' //  PUT  /comercial/clientes/:id/franqueadora            exigirAdmin('COMERCIAL')
  | 'catalogo.gravar' //       POST/PATCH planos, PUT itens/:codigo, PATCH ciclos   exigirAdmin('ADMIN')
  // Contratos e cobrança (W3). Ler — GET /comercial/clientes/:id/empresas, /contratos[/:id], os dois
  // "simular", o GET /faturas/:id/boleto.pdf, o GET /comercial/contratantes?clienteId= e o GET
  // /cobranca/situacao — é exigirAdmin() (toda a equipe): fica no `comercial.ver`.
  | 'contratos.criar' //       POST /comercial/contratos                            exigirAdmin('COMERCIAL')
  | 'contratos.liberar' //     POST …/contratos/:id/liberar                         exigirAdmin('FINANCEIRO')
  | 'contratos.suspender' //   POST …/contratos/:id/suspender e /reativar           exigirAdmin('FINANCEIRO')
  | 'contratos.encerrar' //    POST …/contratos/:id/encerrar                        exigirAdmin('COMERCIAL', 'FINANCEIRO')
  | 'contratos.alterar' //     POST …/contratos/:id/alterar                         exigirAdmin('COMERCIAL')
  | 'contratos.cancelarAlteracao' // POST …/contratos/:id/alteracoes/:a/cancelar    exigirAdmin('COMERCIAL')
  | 'contratos.pagador' //     POST …/contratos/:id/pagador                         exigirAdmin('COMERCIAL', 'FINANCEIRO')
  | 'contratos.forma' //       POST …/contratos/:id/forma                           exigirAdmin('FINANCEIRO')
  | 'faturas.baixaManual' //   POST …/faturas/:id/baixa-manual                      exigirAdmin('FINANCEIRO')
  | 'cobranca.gerar' //        POST …/faturas/:id/cobranca                          exigirAdmin('FINANCEIRO', 'COMERCIAL')
  | 'cobranca.carne' //        POST …/contratos/:id/carne                           exigirAdmin('FINANCEIRO')
  | 'cobranca.cartao' //       POST …/contratos/:id/cartao                          exigirAdmin('FINANCEIRO')
  | 'cobranca.conferir' //     POST …/contratos/:id/conferir                        exigirAdmin('FINANCEIRO', 'SUPORTE')
  // Contratos negociados para redes (comercial/{redes,aplicarTabela,redesPagador}.ts). Ler — GET /comercial/redes/:id,
  // POST …/aplicar-tabela/simular e GET …/pagador — é exigirAdmin() (toda a equipe): fica no `comercial.ver`.
  | 'rede.tabela' //           PUT  /comercial/redes/:clienteId                     exigirAdmin('ADMIN')
  | 'rede.aplicarTabela' //    POST /comercial/redes/:clienteId/aplicar-tabela      exigirAdmin('COMERCIAL')
  | 'rede.pagador' //          PUT  …/redes/:clienteId/pagador e POST …/pagador/aplicar  exigirAdmin('COMERCIAL', 'FINANCEIRO')
  // Cadastros globais e integrações (W4)
  | 'globais.ver' //           GET  /globais/{paises,estados,cidades,unidades,cfops} exigirAdmin()
  | 'globais.gravar' //        PUT  /globais/{…}/:codigo                            exigirAdmin('ADMIN')
  | 'aplicativos.ver' //       GET  /integracoes/aplicativos                        exigirAdmin()
  | 'aplicativos.gravar' //    PUT  /integracoes/aplicativos/:chave                 exigirAdmin('ADMIN')
  | 'parceiras.ver' //         GET  /integracoes/parceiras                          exigirAdmin('SUPORTE')
  | 'parceiras.gravar' //      PUT  /integracoes/parceiras/:aplicativo/:ambiente    exigirAdmin('ADMIN')
  // Suporte (W5). Configurar (o acesso CONFIGURAR e a entrada na loja) NÃO é do papel: é o nível da pessoa —
  // ver `podeConfigurarSuporte` abaixo.
  | 'suporte.ver' //           GET /suporte/acessos, as consultas do cliente (resumo, pessoas, convites, vendas e a venda, integrações),
  //                           POST /suporte/clientes/:id/acesso (LEITURA) e encerrar o próprio  exigirAdmin()
  | 'suporte.todos' //         ver e encerrar os acessos de toda a equipe: GET /suporte/acessos e POST
  //                           …/acessos/:id/encerrar olham se o papel é ADMIN (os outros, só os seus)
  | 'atualizacao.gravar' //    PUT /suporte/instaladores/terminal/atualizacao (pausar, obrigatória, mínima)  exigirAdmin('SUPORTE')
  // PlugAI (Fase D3 da API, src/admin/plugai/rotas.ts)
  | 'plugai.ver' //            GET  /plugai, /plugai/consumo, /plugai/carteiras[/:clienteId]  exigirAdmin()
  | 'plugai.gravar' //         PUT /plugai/config; POST/PUT motores, pacotes, checks, agentes  exigirAdmin('ADMIN')
  | 'plugai.creditos' //       POST …/carteiras/:id/lancamentos, PUT …/habilitado, POST …/openrouter/sincronizar
  // Servidores (monitoramento, src/admin/monitoramento/rotas.ts)
  | 'servidores.ver' //        GET /monitoramento/{painel,historico,erros,alertas,servidores,avisos}  exigirAdmin('SUPORTE')
  | 'servidores.logs' //       POST /monitoramento/logs, GET /monitoramento/logs/:id  exigirAdmin('SUPORTE')
  // O quadro de melhorias (src/admin/melhorias/rotas.ts). Ler — GET /melhorias[/:id] — é exigirAdmin() (toda a equipe).
  | 'melhorias.ver' //         GET  /melhorias[/:id]                                exigirAdmin()
  | 'melhorias.moderar' //     POST/PUT /melhorias, aprovar, recusar, situação, mesclar  exigirAdmin('SUPORTE')
  | 'servidores.configurar'; // PUT /monitoramento/avisos, POST …/avisos/teste, POST/PATCH …/servidores, POST …/servidores/:id/chave  exigirAdmin('ADMIN')
  //                           exigirAdmin('FINANCEIRO', 'COMERCIAL')

const TODOS: Papel[] = ['ADMIN', 'SUPORTE', 'FINANCEIRO', 'COMERCIAL'];

/** Quem pode, ALÉM do ADMIN (que pode tudo) — o mesmo desenho do `exigirAdmin` da API. */
const QUEM_PODE: Record<Capacidade, Papel[]> = {
  'equipe.ver': TODOS,
  'equipe.gravar': [],
  'auditoria.ver': [],
  'comercial.ver': TODOS,
  'contratantes.gravar': ['COMERCIAL'],
  'cliente.criar': ['COMERCIAL'],
  'cliente.franqueadora': ['COMERCIAL'],
  'catalogo.gravar': [],
  'contratos.criar': ['COMERCIAL'],
  'contratos.liberar': ['FINANCEIRO'],
  'contratos.suspender': ['FINANCEIRO'],
  'contratos.encerrar': ['COMERCIAL', 'FINANCEIRO'],
  'contratos.alterar': ['COMERCIAL'],
  'contratos.cancelarAlteracao': ['COMERCIAL'],
  'contratos.pagador': ['COMERCIAL', 'FINANCEIRO'],
  'contratos.forma': ['FINANCEIRO'],
  'faturas.baixaManual': ['FINANCEIRO'],
  'cobranca.gerar': ['FINANCEIRO', 'COMERCIAL'],
  'cobranca.carne': ['FINANCEIRO'],
  'cobranca.cartao': ['FINANCEIRO'],
  'cobranca.conferir': ['FINANCEIRO', 'SUPORTE'],
  'rede.tabela': [],
  'rede.aplicarTabela': ['COMERCIAL'],
  'rede.pagador': ['COMERCIAL', 'FINANCEIRO'],
  'globais.ver': TODOS,
  'globais.gravar': [],
  'aplicativos.ver': TODOS,
  'aplicativos.gravar': [],
  'parceiras.ver': ['SUPORTE'],
  'parceiras.gravar': [],
  'suporte.ver': TODOS,
  'suporte.todos': [],
  'atualizacao.gravar': ['SUPORTE'],
  'plugai.ver': TODOS,
  'plugai.gravar': [],
  'plugai.creditos': ['FINANCEIRO', 'COMERCIAL'],
  'servidores.ver': ['SUPORTE'],
  'servidores.logs': ['SUPORTE'],
  'servidores.configurar': [],
  'melhorias.ver': TODOS,
  'melhorias.moderar': ['SUPORTE'],
};

export function pode(papel: Papel | null | undefined, capacidade: Capacidade): boolean {
  if (!papel) return false;
  return papel === 'ADMIN' || QUEM_PODE[capacidade].includes(papel);
}

/**
 * Configurar no suporte — abrir o acesso no modo CONFIGURAR (`POST /suporte/clientes/:id/acesso`) e gerar a
 * entrada no PlugChef web do cliente (`POST /suporte/acessos/:id/entrada`). A API não olha o papel: olha o
 * NÍVEL da pessoa (`admin.usuarios.suporte_nivel`, que o `GET /auth/eu` traz em `suporteNivel`) — 403
 * SEM_NIVEL_CONFIGURAR sem ele. O ADMIN pode sempre. `nivel` null = ainda não chegou (não mostra).
 */
export function podeConfigurarSuporte(papel: Papel | null | undefined, nivel: NivelSuporte | null | undefined): boolean {
  if (!papel) return false;
  return papel === 'ADMIN' || nivel === 'CONFIGURAR';
}

export const PAPEIS: Papel[] = TODOS;

export const NOME_PAPEL: Record<Papel, string> = {
  ADMIN: 'Administrador',
  SUPORTE: 'Suporte',
  FINANCEIRO: 'Financeiro',
  COMERCIAL: 'Comercial',
};

export function nomePapel(p: string): string {
  return NOME_PAPEL[p as Papel] ?? p;
}

/** O que cada papel faz, em uma frase (a regra é a das rotas da plugchef.admin.api). */
export const RESUMO_PAPEL: Record<Papel, string> = {
  ADMIN:
    'Pode tudo: a equipe, a auditoria, os preços dos planos e a tabela de preços negociada das redes, os cadastros globais, os aplicativos das integrações, as credenciais dos parceiros, o catálogo do PlugAI, os avisos dos servidores e as chaves dos coletores, e o suporte aos clientes (inclusive configurar a loja e encerrar o acesso de qualquer pessoa).',
  SUPORTE:
    'Atende os clientes: abre um acesso com motivo para ver os dados de um cliente e, com o nível "Configurar", entra na loja para configurar. Vê as credenciais dos parceiros, sem alterar, e confere os pagamentos de um contrato. Acompanha os servidores, os erros e os avisos e pede os logs. Modera o quadro de melhorias das lojas.',
  FINANCEIRO:
    'Cuida da cobrança: libera, suspende, reativa e encerra contratos, dá baixa manual em fatura, gera a cobrança, o carnê e o link do cartão, confere os pagamentos, troca a forma de pagamento e o pagador, define quem paga as lojas de uma rede, e cuida dos créditos do PlugAI (bônus, ajuste, estorno e habilitar).',
  COMERCIAL:
    'Vende: cadastra contratantes, cria o cliente com o convite do dono, contrata, altera e encerra contratos, gera a cobrança de uma fatura, troca o pagador, aplica a tabela de preços da rede às lojas e define quem paga cada loja, e cuida dos créditos do PlugAI (bônus, ajuste, estorno e habilitar).',
};

export const NOME_NIVEL_SUPORTE = {
  LEITURA: 'Só ver',
  CONFIGURAR: 'Ver e configurar',
} as const;
